import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog } from '@progress/kendo-react-dialogs';
import { lettersNumbersAndSpecialsRegex } from 'app/common/utils';
import { SubmitButton, TextField } from 'app/components/form-fields';
import { BackActionDialogContentLayout } from 'app/components/layouts';
import useClickOutside from 'app/hooks/useClickOutside';
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import z from 'zod';
import './EditFleetNameDialog.less';

interface Props {
  initialValue: string;
  isLoading?: boolean;
  onChange: (value: string) => void;
  onClose: () => void;
}

const schema = z.object({
  name: z
    .string()
    .trim()
    .min(1, {
      error: 'access_sharing.dialogs.edit_fleet_name.field_is_required',
    })
    .max(64, { error: 'maximum_length_exceeded' })
    .regex(lettersNumbersAndSpecialsRegex),
});

type FormData = z.infer<typeof schema>;

export default function EditFleetNameDialog({
  initialValue,
  isLoading,
  onChange,
  onClose,
}: Props) {
  const { t } = useTranslation();

  // Note: there's a point in creating a wrapper around the Dialog and adding an onClickOutside prop
  useClickOutside(onClose);

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    values: {
      name: initialValue,
    },
  });

  async function onSubmit({ name }: FormData) {
    onChange(name);
  }

  return (
    <Dialog className="modal-container">
      <BackActionDialogContentLayout
        className="edit-fleet-name-dialog"
        onBack={onClose}
      >
        <h2 className="edit-fleet-name-dialog__title">
          {t('access_sharing.dialogs.edit_fleet_name.title')}
        </h2>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t('access_sharing.dialogs.edit_fleet_name.label')}
                required
                placeholder={t(
                  'access_sharing.dialogs.edit_fleet_name.placeholder',
                )}
                disabled={isLoading}
                error={
                  errors.name?.message
                    ? t(errors.name.message, { length: 32 })
                    : undefined
                }
                onChange={e => {
                  const nextValue = e.value || '';

                  const cleanValue = nextValue
                    .split('')
                    .filter(char => lettersNumbersAndSpecialsRegex.test(char))
                    .join('');

                  field.onChange(cleanValue);
                }}
              />
            )}
          />

          <SubmitButton disabled={isLoading} type="submit">
            {t('save')}
          </SubmitButton>
        </form>
      </BackActionDialogContentLayout>
    </Dialog>
  );
}
